import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../api/apiConfig';
import './SpeechModal.css';

/**
 * SpeechModal — voice input modal with live transcription.
 *
 * Flow:
 *   1. Opens → recording starts automatically
 *   2. Browser SpeechRecognition streams interim + final text live
 *      (Whisper fallback for browsers without SpeechRecognition)
 *   3. User clicks Stop → transitions to editing state
 *   4. User edits the textarea → Confirm → onConfirm(text) called → modal closes
 *
 * Props:
 *   isOpen         (bool)     — controls visibility
 *   onClose        (fn)       — called on Cancel / backdrop click
 *   onConfirm      (fn)       — called with final text string
 *   fieldLabel     (string)   — shown in header, e.g. "Message"
 *   accessToken    (string)   — Bearer token for the Whisper fallback API call
 */

const SpeechRecognitionAPI =
  typeof window !== 'undefined' &&
  (window.SpeechRecognition || window.webkitSpeechRecognition);

const SpeechModal = ({
  isOpen,
  onClose,
  onConfirm,
  fieldLabel = 'Field',
  accessToken = null,
}) => {
  // 'recording' | 'transcribing' | 'editing'
  const [phase, setPhase] = useState('recording');
  const [finalText, setFinalText] = useState('');
  const [interimText, setInterimText] = useState('');
  const [editableText, setEditableText] = useState('');
  const [error, setError] = useState('');

  // SpeechRecognition path
  const recognitionRef = useRef(null);
  // Whisper fallback path
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const mimeTypeRef = useRef('');

  // ── Cleanup helper ──────────────────────────────────────────────────────
  const cleanup = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (_) {}
      recognitionRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (_) {}
    }
    mediaRecorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    chunksRef.current = [];
  }, []);

  // ── SpeechRecognition path ───────────────────────────────────────────────
  const startSpeechRecognition = useCallback(() => {
    const recognition = new SpeechRecognitionAPI();
    recognitionRef.current = recognition;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    let accumulated = '';

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          accumulated += result[0].transcript;
          setFinalText(accumulated);
        } else {
          interim += result[0].transcript;
        }
      }
      setInterimText(interim);
    };

    recognition.onend = () => {
      // Browser stopped (silence timeout) — move to editing automatically
      setInterimText('');
      setEditableText(accumulated.trim());
      setPhase('editing');
    };

    recognition.onerror = (e) => {
      if (e.error === 'not-allowed') {
        setError('Microphone access denied. Please allow microphone access.');
      } else if (e.error !== 'aborted') {
        setError(`Recognition error: ${e.error}`);
      }
      setPhase('editing');
      setEditableText(accumulated.trim());
    };

    recognition.start();
  }, []);

  // ── Whisper fallback path ────────────────────────────────────────────────
  const startWhisperRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/ogg;codecs=opus';
      mimeTypeRef.current = mimeType;

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setPhase('transcribing');
        try {
          const ext = mimeTypeRef.current.includes('webm') ? 'webm' : 'ogg';
          const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
          const audioFile = new File([blob], `recording.${ext}`, { type: mimeTypeRef.current });

          // Call our backend Whisper endpoint
          const formData = new FormData();
          formData.append('audio', audioFile);
          const headers = {};
          if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
          const response = await fetch(`${API_BASE_URL}/api/transcribe`, {
            method: 'POST',
            headers,
            body: formData,
          });
          if (!response.ok) throw new Error(`Server error ${response.status}`);
          const data = await response.json();
          setEditableText((data.text || '').trim());
        } catch {
          setError('Transcription failed. You can type your text manually.');
        } finally {
          setPhase('editing');
        }
      };

      recorder.start();
    } catch {
      setError('Microphone access denied. Please allow microphone access and try again.');
      setPhase('editing');
    }
  }, [accessToken]);

  // ── Start recording when modal opens ────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    // Reset state
    setPhase('recording');
    setFinalText('');
    setInterimText('');
    setEditableText('');
    setError('');
    chunksRef.current = [];

    if (SpeechRecognitionAPI) {
      startSpeechRecognition();
    } else {
      startWhisperRecording();
    }

    return () => cleanup();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Stop button (recording phase) ────────────────────────────────────────
  const handleStop = () => {
    if (SpeechRecognitionAPI && recognitionRef.current) {
      // Stopping recognition triggers onend → editing phase automatically
      recognitionRef.current.stop();
    } else if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      // onstop handler will set phase → transcribing → editing
    }
  };

  // ── Re-record ────────────────────────────────────────────────────────────
  const handleReRecord = () => {
    cleanup();
    setFinalText('');
    setInterimText('');
    setEditableText('');
    setError('');
    setPhase('recording');

    if (SpeechRecognitionAPI) {
      startSpeechRecognition();
    } else {
      startWhisperRecording();
    }
  };

  // ── Confirm ──────────────────────────────────────────────────────────────
  const handleConfirm = () => {
    const text = editableText.trim();
    if (text) onConfirm(text);
    handleClose();
  };

  // ── Close / Cancel ───────────────────────────────────────────────────────
  const handleClose = () => {
    cleanup();
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="speech-modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Voice input"
    >
      <div className="speech-modal">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="speech-modal-header">
          <span className="speech-modal-title">
            {phase === 'recording' && (
              <span className="recording-dot" aria-hidden="true" />
            )}
            {phase === 'recording'
              ? 'Listening…'
              : phase === 'transcribing'
              ? 'Transcribing…'
              : 'Review & Confirm'}
          </span>
          <span className="speech-modal-field-label">Speaking into: {fieldLabel}</span>
          <button className="speech-modal-close" onClick={handleClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────────── */}
        <div className="speech-modal-body">
          {/* Recording phase — live transcript display */}
          {phase === 'recording' && (
            <>
              <div
                className="live-transcript"
                aria-live="polite"
                aria-label="Live transcription"
              >
                {!finalText && !interimText && (
                  <span className="transcript-placeholder">Start speaking…</span>
                )}
                <span className="final-text">{finalText}</span>
                {interimText && (
                  <span className="interim-text"> {interimText}</span>
                )}
              </div>
              <div className="speech-modal-actions">
                <button className="btn-stop" onClick={handleStop}>
                  <span className="stop-icon" aria-hidden="true" />
                  Stop
                </button>
              </div>
            </>
          )}

          {/* Transcribing phase (Whisper fallback) */}
          {phase === 'transcribing' && (
            <div className="transcribing-indicator">
              <span className="transcribing-spinner" />
              <span>Transcribing your recording…</span>
            </div>
          )}

          {/* Editing phase */}
          {phase === 'editing' && (
            <>
              {error && (
                <div className="speech-modal-error" role="alert">
                  {error}
                </div>
              )}
              <textarea
                className="speech-edit-textarea"
                value={editableText}
                onChange={(e) => setEditableText(e.target.value)}
                rows={6}
                placeholder="Your transcription will appear here. You can edit it before confirming."
                autoFocus
              />
              <div className="speech-modal-actions">
                <button className="btn-rerecord" onClick={handleReRecord}>
                  Re-record
                </button>
                <div className="speech-modal-actions-right">
                  <button className="btn-cancel" onClick={handleClose}>
                    Cancel
                  </button>
                  <button
                    className="btn-confirm"
                    onClick={handleConfirm}
                    disabled={!editableText.trim()}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpeechModal;
